import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  addSlot as apiAddSlot,
  ApiClientError,
  backfillHutDutyAliases,
  bulkHutDuties,
  createHutDuty,
  deleteHutDuty,
  deleteSlot as apiDeleteSlot,
  listHutDuties,
  patchHutDuty,
  patchSlot as apiPatchSlot,
  type BulkHutDutyEntry,
  type HutDuty,
  type HutDutySlot,
  type NewSlotInput,
} from '../../lib/api';
import {
  ALIAS_RE,
  dayDatesInclusive,
  ERROR_CODES,
  nightDatesBetween,
  randomAlias,
} from '@baerenfang/shared';
import { useDateRange } from '../../lib/useDateRange';
import { DateRangePicker } from '../../components/DateRangePicker';
import { QrCodeModal } from '../../components/QrCodeModal';
import { formatDateShort } from '../../lib/format';
import { todayInZurich } from '../../lib/today';

const blankSlot = (): NewSlotInput => {
  const today = todayInZurich();
  return {
    check_in: today,
    check_out: today,
    note: null,
    beds_per_night: [],
    is_duty_per_day: [],
  };
};

/** Resize/preserve a slot's per-night beds array when its date range changes.
 *  Walks the new nights, copying the value from the old array by date so a
 *  surviving night keeps its value; new nights get 0; dropped nights are
 *  forgotten. */
function reshapeBedsPerNight(
  oldCheckIn: string,
  oldCheckOut: string,
  oldBeds: number[] | undefined,
  newCheckIn: string,
  newCheckOut: string,
): number[] {
  const oldDates = nightDatesBetween(oldCheckIn, oldCheckOut);
  const oldByDate = new Map<string, number>();
  oldDates.forEach((d, i) => oldByDate.set(d, oldBeds?.[i] ?? 0));
  return nightDatesBetween(newCheckIn, newCheckOut).map((d) => oldByDate.get(d) ?? 0);
}

/** True for Saturday/Sunday in the given YYYY-MM-DD. Parses as UTC so
 *  the day-of-week reflects the calendar date (not the viewer's local
 *  tz or DST drift). */
function isWeekendYmd(ymd: string): boolean {
  const dow = new Date(`${ymd}T00:00:00Z`).getUTCDay();
  return dow === 0 || dow === 6;
}

/** Localised 2–3 letter weekday abbreviation for the given YYYY-MM-DD.
 *  Follows the nav-drawer language ("Sa"/"So" in DE, "Sat"/"Sun" in EN). */
function weekdayShort(ymd: string, lang: string): string {
  return new Intl.DateTimeFormat(lang, {
    weekday: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${ymd}T00:00:00Z`));
}

/** Same as reshapeBedsPerNight but for the per-day duty array (inclusive
 *  date domain). Defaults newly-introduced days to `true` so a freshly
 *  picked range starts out "all days are duty" — the common case. */
function reshapeIsDutyPerDay(
  oldCheckIn: string,
  oldCheckOut: string,
  oldDuty: boolean[] | undefined,
  newCheckIn: string,
  newCheckOut: string,
): boolean[] {
  const oldDates = dayDatesInclusive(oldCheckIn, oldCheckOut);
  const oldByDate = new Map<string, boolean>();
  oldDates.forEach((d, i) => oldByDate.set(d, oldDuty?.[i] ?? false));
  const newDates = dayDatesInclusive(newCheckIn, newCheckOut);
  // If no prior values, default-true for every new day.
  if (!oldDuty || oldDuty.length === 0) return newDates.map(() => true);
  return newDates.map((d) => oldByDate.get(d) ?? true);
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const INT_RE = /^\d+$/;

/**
 * Parse one line of the bulk-paste textarea.
 *
 *   "Müller"                                → name only
 *   "Schmidt 2026-05-15 2026-05-17"         → name + slot range, 0 beds
 *   "Schmidt 2026-05-15 2026-05-17 4"       → name + slot range, 4 beds
 *   "Keller von Lichtensteig"               → name with spaces (no dates)
 *
 * Trailing two YYYY-MM-DD tokens (first < second) become the slot range.
 * An additional trailing integer token before them is taken as the bed
 * count for that slot. Without dates, neither beds nor any other slot
 * fields are accepted. Slot defaults: is_duty=true, note=null.
 *
 * Returns null for blank lines so callers can filter them out.
 */
function parseBulkLine(line: string): BulkHutDutyEntry | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  const tokens = trimmed.split(/\s+/);

  const matchDates = (
    a: string | undefined,
    b: string | undefined,
  ): a is string => !!a && !!b && DATE_RE.test(a) && DATE_RE.test(b) && a < b;

  // Try: name … checkIn checkOut beds. Trailing integer is treated as
  // "this number on every night the slot covers" — parser expands it to
  // a flat array. Per-night refinement happens admin-UI-only.
  if (tokens.length >= 4) {
    const beds = tokens[tokens.length - 1]!;
    const checkOut = tokens[tokens.length - 2]!;
    const checkIn = tokens[tokens.length - 3]!;
    if (INT_RE.test(beds) && matchDates(checkIn, checkOut)) {
      const name = tokens.slice(0, -3).join(' ');
      const value = Number.parseInt(beds, 10);
      const beds_per_night = nightDatesBetween(checkIn, checkOut).map(() => value);
      const is_duty_per_day = dayDatesInclusive(checkIn, checkOut).map(() => true);
      return {
        name,
        slot: {
          check_in: checkIn,
          check_out: checkOut,
          note: null,
          beds_per_night,
          is_duty_per_day,
        },
      };
    }
  }

  // Try: name … checkIn checkOut
  if (tokens.length >= 3) {
    const checkOut = tokens[tokens.length - 1]!;
    const checkIn = tokens[tokens.length - 2]!;
    if (matchDates(checkIn, checkOut)) {
      const name = tokens.slice(0, -2).join(' ');
      const is_duty_per_day = dayDatesInclusive(checkIn, checkOut).map(() => true);
      return {
        name,
        slot: {
          check_in: checkIn,
          check_out: checkOut,
          note: null,
          beds_per_night: [],
          is_duty_per_day,
        },
      };
    }
  }

  return { name: trimmed };
}

const PAGE_SIZE = 15;

// Sentinel pushes rows with no (visible) slots to the end of any chronological sort.
const SLOT_DATE_SENTINEL = '￿';

/** Earliest `check_in` across the given slots, or a sentinel that sorts last.
 *  Caller passes either `r.slots` (sort by all slots) or `visibleSlots(r)`
 *  (sort by what the user currently sees). */
function earliestSlotDate(slots: HutDutySlot[]): string {
  if (slots.length === 0) return SLOT_DATE_SENTINEL;
  return slots.reduce(
    (min, s) => (s.check_in < min ? s.check_in : min),
    slots[0]!.check_in,
  );
}

export function AdminHutDutyTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const [searchParams, setSearchParams] = useSearchParams();

  // Hand-off from the Calendar tab: `?slot=<id>&name=<name>` pre-fills the
  // name filter and auto-opens that specific slot's edit form. Captured
  // once on mount; the URL is then stripped so refresh/back/forward behave
  // canonically.
  const handoffRef = useRef<{ slotId: number | null; name: string }>({
    slotId: (() => {
      const raw = searchParams.get('slot');
      const n = raw ? Number.parseInt(raw, 10) : Number.NaN;
      return Number.isFinite(n) ? n : null;
    })(),
    name: searchParams.get('name') ?? '',
  });

  const [rows, setRows] = useState<HutDuty[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState(handoffRef.current.name);
  const {
    from: fromFilter,
    to: toFilter,
    reset: resetDateFilter,
  } = useDateRange();
  // Default to the "active slots only" view — the warden almost always
  // wants to see who's coming up, not the whole historical roster.
  const [activeOnly, setActiveOnly] = useState(true);
  const [showPastSlots, setShowPastSlots] = useState(false);
  const [page, setPage] = useState(0);
  const [qrTarget, setQrTarget] = useState<{ name: string; identifier: string } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const todayStr = todayInZurich();
  const visibleSlots = useCallback(
    (r: HutDuty) =>
      showPastSlots ? r.slots : r.slots.filter((s) => s.check_out >= todayStr),
    [showPastSlots, todayStr],
  );

  // Strip the hand-off params from the URL once consumed.
  useEffect(() => {
    if (searchParams.get('slot') || searchParams.get('name')) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply both filters, then sort by the row's earliest slot date ascending
  // (rows with no slots fall to the end, name as tiebreaker). Name uses
  // case-insensitive substring; the from–to date range keeps any row whose
  // visible slots overlap [fromFilter, toFilter]. "Visible" excludes past
  // slots when showPastSlots is off — so toggling that flag also widens or
  // narrows the searchable set.
  const filtered = useMemo(() => {
    const needle = nameFilter.trim().toLowerCase();
    const lo = fromFilter || '0000-00-00';
    const hi = toFilter || '9999-12-31';
    const rangeActive = !!(fromFilter || toFilter);
    const matched = rows.filter((r) => {
      if (needle && !r.name.toLowerCase().includes(needle)) return false;
      // "Active" = at least one slot ending today or later. Independent of
      // showPastSlots (which only affects per-row slot rendering).
      if (activeOnly) {
        const hasFuture = r.slots.some((s) => s.check_out >= todayStr);
        if (!hasFuture) return false;
      }
      if (rangeActive) {
        const candidates = visibleSlots(r);
        const hit = candidates.some((s) => s.check_in <= hi && s.check_out >= lo);
        if (!hit) return false;
      }
      return true;
    });
    // Sort key respects the visibility checkboxes — past-only or slot-less
    // rows fall to the end via the sentinel, and rows with both past and
    // future slots sort by their earliest *visible* slot.
    return matched.slice().sort((a, b) => {
      const da = earliestSlotDate(visibleSlots(a));
      const db = earliestSlotDate(visibleSlots(b));
      if (da !== db) return da.localeCompare(db);
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [rows, nameFilter, fromFilter, toFilter, activeOnly, todayStr, visibleSlots]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // Reset page to 0 whenever filters change.
  useEffect(() => {
    setPage(0);
  }, [nameFilter, fromFilter, toFilter, activeOnly, showPastSlots]);

  // Clamp page when data shrinks under us (e.g. after a delete).
  useEffect(() => {
    if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);

  // On first data load, jump to the page containing the row whose earliest
  // slot is closest to today (forward-leaning: first row with earliest >=
  // today, else the last row that has slots at all). One-shot — once the
  // user has started paging or filtering manually, leave their page alone.
  // Deep-link handoffs from the Calendar tab arrive with a filter set and
  // are handled by the filter-reset-to-0 effect above, so skip the jump
  // when any filter is already active on first load.
  const initialJumpDoneRef = useRef(false);
  useEffect(() => {
    if (initialJumpDoneRef.current) return;
    if (filtered.length === 0) return;
    initialJumpDoneRef.current = true;
    if (nameFilter || fromFilter || toFilter) return;
    const today = todayInZurich();
    // Same visibility lens the sort uses, so the init-jump lands on the page
    // the warden actually sees as "today's row".
    const withVisibleSlots = filtered.filter((r) => visibleSlots(r).length > 0);
    if (withVisibleSlots.length === 0) return;
    const target =
      withVisibleSlots.find((r) => earliestSlotDate(visibleSlots(r)) >= today) ??
      withVisibleSlots[withVisibleSlots.length - 1]!;
    const idx = filtered.indexOf(target);
    if (idx >= 0) setPage(Math.floor(idx / PAGE_SIZE));
  }, [filtered, nameFilter, fromFilter, toFilter]);

  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const clearFilters = () => {
    setNameFilter('');
    resetDateFilter('', '');
    setActiveOnly(false);
  };

  // One-shot guard so the backfill only runs once per admin session even
  // if `refresh` is called repeatedly.
  const backfilledRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const fresh = await listHutDuties();
      setRows(fresh);
      // Legacy rows that pre-date the login_alias column come back with
      // `login_alias: null`. Fill them in once, then re-fetch so the UI
      // reflects the generated aliases.
      if (!backfilledRef.current && fresh.some((r) => r.login_alias == null)) {
        backfilledRef.current = true;
        try {
          const { filled } = await backfillHutDutyAliases();
          if (filled > 0) {
            const refilled = await listHutDuties();
            setRows(refilled);
            setError(t('admin.hutDuty.aliasesGenerated', { count: filled }));
          }
        } catch {
          // Non-fatal; the warden can re-trigger by reloading or by
          // editing rows manually.
        }
      }
    } catch (e) {
      if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
      else setError(t('errors.ERR_INTERNAL'));
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggleActive = async (row: HutDuty) => {
    try {
      const updated = await patchHutDuty(row.id, { active: !row.active });
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, ...updated, slots: r.slots } : r)),
      );
    } catch (e) {
      if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
    }
  };

  // Two-stage on purpose. The first confirm covers removing the name. If the
  // server reports duty slots hanging off it, the second names how many are
  // about to be destroyed before we opt in to the cascade -- deleting a name is
  // cheap, deleting a duty history is not, and the two should not feel alike.
  const removeDuty = async (row: HutDuty) => {
    if (!confirm(t('admin.hutDuty.confirmDelete', { name: row.name }))) return;
    const drop = async (cascade: boolean) => {
      await deleteHutDuty(row.id, cascade ? { cascade: true } : undefined);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    };
    try {
      await drop(false);
    } catch (e) {
      if (!(e instanceof ApiClientError)) return;
      if (e.code !== ERROR_CODES.ERR_NAME_HAS_DUTY_SLOTS) {
        setError(t(`errors.${e.code}`, e.message));
        return;
      }
      const slots = (e.details as { slots?: number } | undefined)?.slots ?? row.slots.length;
      const msg = t('admin.hutDuty.confirmDeleteWithSlots', { name: row.name, count: slots });
      if (!confirm(msg)) return;
      try {
        await drop(true);
      } catch (err) {
        if (err instanceof ApiClientError) setError(t(`errors.${err.code}`, err.message));
      }
    }
  };

  const editAlias = async (dutyId: number, next: string | null) => {
    try {
      const updated = await patchHutDuty(dutyId, { login_alias: next });
      setRows((prev) =>
        prev.map((r) => (r.id === dutyId ? { ...r, ...updated, slots: r.slots } : r)),
      );
    } catch (e) {
      if (e instanceof ApiClientError) {
        // Bubble up to the row's inline editor so it can show "already taken".
        throw e;
      }
      throw new Error('ERR_INTERNAL');
    }
  };

  // Inline-edit of the master row's name; surfaces 409 on the unique-name
  // collision via the same try/catch that protects the alias path.
  const editName = async (dutyId: number, next: string) => {
    try {
      const updated = await patchHutDuty(dutyId, { name: next });
      setRows((prev) =>
        prev.map((r) => (r.id === dutyId ? { ...r, ...updated, slots: r.slots } : r)),
      );
    } catch (e) {
      if (e instanceof ApiClientError) throw e;
      throw new Error('ERR_INTERNAL');
    }
  };

  // Inline-edit of the master row's note. `null` clears the note.
  const editNote = async (dutyId: number, next: string | null) => {
    try {
      const updated = await patchHutDuty(dutyId, { note: next });
      setRows((prev) =>
        prev.map((r) => (r.id === dutyId ? { ...r, ...updated, slots: r.slots } : r)),
      );
    } catch (e) {
      if (e instanceof ApiClientError) throw e;
      throw new Error('ERR_INTERNAL');
    }
  };

  const addSlot = async (dutyId: number, slot: NewSlotInput) => {
    if (slot.check_in >= slot.check_out) {
      setError(t('admin.hutDuty.slotInvalid'));
      return;
    }
    try {
      const created = await apiAddSlot(dutyId, slot);
      setRows((prev) =>
        prev.map((r) => (r.id === dutyId ? { ...r, slots: [...r.slots, created] } : r)),
      );
    } catch (e) {
      if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
    }
  };

  const editSlot = async (dutyId: number, slotId: number, next: NewSlotInput) => {
    if (next.check_in >= next.check_out) {
      setError(t('admin.hutDuty.slotInvalid'));
      return;
    }
    try {
      const updated = await apiPatchSlot(dutyId, slotId, next);
      setRows((prev) =>
        prev.map((r) =>
          r.id === dutyId
            ? { ...r, slots: r.slots.map((s) => (s.id === slotId ? updated : s)) }
            : r,
        ),
      );
    } catch (e) {
      if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
    }
  };

  const removeSlot = async (dutyId: number, slot: HutDutySlot) => {
    if (
      !confirm(
        t('admin.hutDuty.confirmDeleteSlot', {
          from: formatDateShort(slot.check_in, lang),
          to: formatDateShort(slot.check_out, lang),
        }),
      )
    )
      return;
    try {
      await apiDeleteSlot(dutyId, slot.id);
      setRows((prev) =>
        prev.map((r) =>
          r.id === dutyId ? { ...r, slots: r.slots.filter((s) => s.id !== slot.id) } : r,
        ),
      );
    } catch (e) {
      if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => setBulkOpen(true)}
          className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm font-medium"
        >
          {t('admin.hutDuty.bulkImport')}
        </button>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium"
        >
          + {t('admin.hutDuty.add')}
        </button>
      </div>

      {error && (
        <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      <div className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-3 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 sm:items-end">
          <label className="text-sm">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">
              {t('admin.hutDuty.filterName')}
            </span>
            <input
              type="search"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder={t('admin.hutDuty.filterNamePlaceholder')}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-sm min-w-0">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">
              {t('admin.hutDuty.from')} – {t('admin.hutDuty.to')}
            </span>
            <DateRangePicker
              from={fromFilter}
              to={toFilter}
              onChange={resetDateFilter}
            />
          </label>
          <button
            type="button"
            onClick={clearFilters}
            disabled={!nameFilter && !fromFilter && !toFilter && !activeOnly}
            className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            {t('admin.hutDuty.clearFilters')}
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          <span>{t('admin.hutDuty.activeSlots')}</span>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={showPastSlots}
            onChange={(e) => setShowPastSlots(e.target.checked)}
          />
          <span>{t('admin.hutDuty.showPast')}</span>
        </label>
      </div>

      <div className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg">
        {rows.length === 0 ? (
          <p className="p-4 text-center text-slate-500 text-sm dark:text-slate-400">
            {t('admin.hutDuty.empty')}
          </p>
        ) : filtered.length === 0 ? (
          <p className="p-4 text-center text-slate-500 text-sm dark:text-slate-400">
            {t('admin.hutDuty.noMatches')}
          </p>
        ) : (
          pageRows.map((r) => (
            <DutyRow
              key={r.id}
              row={r}
              displaySlots={visibleSlots(r)}
              lang={lang}
              targetSlotId={handoffRef.current.slotId}
              onToggleActive={() => void toggleActive(r)}
              onDelete={() => void removeDuty(r)}
              onAddSlot={(slot) => void addSlot(r.id, slot)}
              onEditSlot={(slotId, next) => void editSlot(r.id, slotId, next)}
              onDeleteSlot={(slot) => void removeSlot(r.id, slot)}
              onEditAlias={(next) => editAlias(r.id, next)}
              onEditName={(next) => editName(r.id, next)}
              onEditNote={(next) => editNote(r.id, next)}
              onQr={() =>
                setQrTarget({ name: r.name, identifier: r.login_alias ?? r.name })
              }
            />
          ))
        )}
      </div>

      {filtered.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 text-sm text-slate-700 dark:text-slate-300">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {t('admin.hutDuty.pageOf', { page: page + 1, total: totalPages })}
            {' · '}
            {t('admin.hutDuty.pageRange', {
              from: page * PAGE_SIZE + 1,
              to: Math.min((page + 1) * PAGE_SIZE, filtered.length),
              count: filtered.length,
            })}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-xs font-medium disabled:opacity-40"
              aria-label={t('admin.hutDuty.previousPage')}
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-xs font-medium disabled:opacity-40"
              aria-label={t('admin.hutDuty.nextPage')}
            >
              →
            </button>
          </div>
        </div>
      )}

      {qrTarget != null && (
        <QrCodeModal
          name={qrTarget.name}
          identifier={qrTarget.identifier}
          onClose={() => setQrTarget(null)}
        />
      )}

      {addOpen && (
        <NewHutDutyDrawer
          existingNames={rows.map((r) => ({ id: r.id, name: r.name }))}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            void refresh();
          }}
        />
      )}

      {bulkOpen && (
        <BulkImportDrawer
          onClose={() => setBulkOpen(false)}
          onSaved={(skipped) => {
            setBulkOpen(false);
            if (skipped > 0) {
              setError(t('admin.hutDuty.bulkFailed', { count: skipped }));
            }
            void refresh();
          }}
        />
      )}
    </div>
  );
}

export type ExistingNameRef = { id: number; name: string };

function AddRowForm({
  onAdd,
  busy,
  existingNames,
  initialSlot,
}: {
  onAdd(
    name: string,
    note: string | null,
    slot: NewSlotInput,
    login_alias: string | null,
    existingId: number | null,
  ): Promise<void>;
  busy: boolean;
  existingNames: ExistingNameRef[];
  initialSlot?: NewSlotInput;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [alias, setAlias] = useState('');
  const [slot, setSlot] = useState<NewSlotInput>(initialSlot ?? blankSlot());
  const [nameFocused, setNameFocused] = useState(false);

  const sortedNames = useMemo(
    () => [...existingNames].sort((a, b) => a.name.localeCompare(b.name)),
    [existingNames],
  );
  const matchedExisting = useMemo(() => {
    const needle = name.trim().toLocaleLowerCase();
    if (!needle) return null;
    return existingNames.find((r) => r.name.toLocaleLowerCase() === needle) ?? null;
  }, [existingNames, name]);
  // Filtered suggestions for the custom dropdown (datalist is unreliable
  // on iOS Safari and Firefox iOS). Show all on focus when the input is
  // empty; substring-filter once the user starts typing. Hide entirely
  // once there's an exact match — the matched-existing hint takes over.
  const suggestions = useMemo(() => {
    if (matchedExisting) return [];
    const needle = name.trim().toLocaleLowerCase();
    if (!needle) return sortedNames;
    return sortedNames.filter((r) => r.name.toLocaleLowerCase().includes(needle));
  }, [matchedExisting, name, sortedNames]);
  const showSuggestions = nameFocused && suggestions.length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        const aliasValue = alias.trim() ? alias.trim() : null;
        const existingId = matchedExisting?.id ?? null;
        void onAdd(name.trim(), note.trim() || null, slot, aliasValue, existingId).then(() => {
          setName('');
          setNote('');
          setAlias('');
          setSlot(blankSlot());
        });
      }}
      className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
    >
      <label className="text-sm sm:col-span-2">
        <span className="block mb-1 text-slate-600 dark:text-slate-400">{t('admin.hutDuty.name')}</span>
        <div className="relative">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={() => setNameFocused(true)}
            onBlur={() => setNameFocused(false)}
            autoComplete="off"
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm"
            required
          />
          {showSuggestions && (
            <ul
              role="listbox"
              className="absolute left-0 right-0 top-full mt-1 z-20 max-h-48 overflow-auto rounded-md border border-slate-300 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800"
            >
              {suggestions.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    role="option"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setName(r.name);
                      setNameFocused(false);
                    }}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700"
                  >
                    {r.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </label>
      {matchedExisting ? (
        <p
          className="sm:col-span-2 text-xs text-slate-600 dark:text-slate-300 bg-sky-50 border border-sky-200 dark:bg-sky-950 dark:border-sky-800 rounded-md px-3 py-2"
          role="status"
        >
          {t('admin.hutDuty.matchedExistingHint', { name: matchedExisting.name })}
        </p>
      ) : (
        <>
          <label className="text-sm sm:col-span-2">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">{t('admin.hutDuty.note')}</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm sm:col-span-2 min-w-0">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">
              {t('admin.hutDuty.alias')}
              <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                {t('admin.hutDuty.aliasHelp')}
              </span>
            </span>
            <div className="flex gap-2">
              <input
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                pattern={ALIAS_RE.source}
                placeholder="alpenrose-12"
                className="flex-1 min-w-0 rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setAlias(randomAlias())}
                className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-2 text-sm"
              >
                {t('admin.hutDuty.aliasSuggest')}
              </button>
            </div>
          </label>
        </>
      )}
      <SlotFields slot={slot} onChange={setSlot} />
      <div className="sm:col-span-2 flex justify-end">
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-4 py-2 text-sm font-medium disabled:opacity-60"
        >
          {t('admin.hutDuty.add')}
        </button>
      </div>
    </form>
  );
}

function SlotFields({
  slot,
  onChange,
}: {
  slot: NewSlotInput;
  onChange(next: NewSlotInput): void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const days = dayDatesInclusive(slot.check_in, slot.check_out);
  // Index of the check_out day inside `days` — last entry. Used to mark
  // the row with a dash in the Betten column (no night for check_out).
  const checkOutIdx = days.length - 1;
  // Slot dates use the booking range semantic (check_out is exclusive),
  // so minSpan = 1 day. The picker's two-click machine enforces
  // check_out >= check_in + 1.
  return (
    <>
      <label className="text-sm col-span-2 min-w-0">
        <span className="block mb-1 text-slate-600 dark:text-slate-400">
          {t('admin.hutDuty.checkIn')} – {t('admin.hutDuty.checkOut')}
        </span>
        <DateRangePicker
          from={slot.check_in}
          to={slot.check_out}
          onChange={(ci, co) =>
            onChange({
              ...slot,
              check_in: ci,
              check_out: co,
              beds_per_night: reshapeBedsPerNight(
                slot.check_in,
                slot.check_out,
                slot.beds_per_night,
                ci,
                co,
              ),
              is_duty_per_day: reshapeIsDutyPerDay(
                slot.check_in,
                slot.check_out,
                slot.is_duty_per_day,
                ci,
                co,
              ),
            })
          }
          minSpanDays={1}
          required
        />
      </label>
      <div className="text-sm sm:col-span-2 min-w-0">
        <span className="block mb-1 text-slate-600 dark:text-slate-400">
          {t('admin.hutDuty.perDayEditorTitle')}
        </span>
        {days.length === 0 ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('admin.hutDuty.bedsPickDates')}
          </p>
        ) : (
          <div className="rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1 px-3 py-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 font-medium">
              <span>{t('admin.hutDuty.perDayDate')}</span>
              <span className="text-center w-20">{t('admin.hutDuty.perDayDuty')}</span>
              <span className="text-right w-20">{t('admin.hutDuty.perDayBeds')}</span>
            </div>
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {days.map((date, i) => {
                const weekend = isWeekendYmd(date);
                return (
                <div
                  key={date}
                  className={
                    'grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1 px-3 py-2 items-center ' +
                    (weekend ? 'bg-amber-50/60 dark:bg-amber-900/15' : '')
                  }
                >
                  <span
                    className={
                      'text-xs tabular-nums ' +
                      (weekend
                        ? 'font-semibold text-amber-900 dark:text-amber-200'
                        : '')
                    }
                  >
                    {weekdayShort(date, lang)} {formatDateShort(date, lang)}
                  </span>
                  <label className="text-center w-20">
                    <input
                      type="checkbox"
                      checked={slot.is_duty_per_day?.[i] ?? false}
                      onChange={(e) => {
                        const next = days.map((_, j) =>
                          j === i ? e.target.checked : slot.is_duty_per_day?.[j] ?? false,
                        );
                        onChange({ ...slot, is_duty_per_day: next });
                      }}
                      aria-label={t('admin.hutDuty.perDayDuty')}
                    />
                  </label>
                  <div className="w-20">
                    {i === checkOutIdx ? (
                      <span className="block text-right text-xs text-slate-400 dark:text-slate-500">
                        —
                      </span>
                    ) : (
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="off"
                        value={slot.beds_per_night?.[i] ?? 0}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, '');
                          const v = Math.min(1000, raw === '' ? 0 : Number.parseInt(raw, 10));
                          // Beds array is indexed by nights, not days, so
                          // the index `i` over `days` happens to match the
                          // nights index for every row except the last
                          // (check_out, which we've already filtered out
                          // via i === checkOutIdx above).
                          const nightsCount = checkOutIdx; // = days.length - 1
                          const next = Array.from({ length: nightsCount }, (_, j) =>
                            j === i ? v : slot.beds_per_night?.[j] ?? 0,
                          );
                          onChange({ ...slot, beds_per_night: next });
                        }}
                        onFocus={(e) => e.currentTarget.select()}
                        className="block w-full max-w-full box-border rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1 text-sm text-right tabular-nums"
                        title={t('admin.hutDuty.bedsHelp')}
                      />
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <label className="text-sm sm:col-span-2 min-w-0">
        <span className="block mb-1 text-slate-600 dark:text-slate-400">{t('admin.hutDuty.slotNote')}</span>
        <input
          value={slot.note ?? ''}
          onChange={(e) => onChange({ ...slot, note: e.target.value || null })}
          className="block w-full max-w-full box-border rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm"
        />
      </label>
    </>
  );
}

function DutyRow({
  row,
  displaySlots,
  lang,
  targetSlotId,
  onToggleActive,
  onDelete,
  onAddSlot,
  onEditSlot,
  onDeleteSlot,
  onEditAlias,
  onEditName,
  onEditNote,
  onQr,
}: {
  row: HutDuty;
  /** Slots to render under the row header. Excludes past slots unless the
   * "Show past slots" toggle in the filter strip is on. */
  displaySlots: HutDutySlot[];
  lang: string;
  targetSlotId: number | null;
  onToggleActive(): void;
  onDelete(): void;
  onAddSlot(slot: NewSlotInput): void;
  onEditSlot(slotId: number, next: NewSlotInput): void;
  onDeleteSlot(slot: HutDutySlot): void;
  onEditAlias(next: string | null): Promise<void>;
  onEditName(next: string): Promise<void>;
  onEditNote(next: string | null): Promise<void>;
  onQr(): void;
}) {
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);
  const [slot, setSlot] = useState<NewSlotInput>(blankSlot());
  const [editingAlias, setEditingAlias] = useState(false);
  const [aliasDraft, setAliasDraft] = useState(row.login_alias ?? '');
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [aliasBusy, setAliasBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(row.name);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameBusy, setNameBusy] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState(row.note ?? '');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteBusy, setNoteBusy] = useState(false);

  const saveName = async () => {
    const next = nameDraft.trim();
    if (!next) {
      setNameError(t('errors.ERR_VALIDATION'));
      return;
    }
    setNameBusy(true);
    setNameError(null);
    try {
      await onEditName(next);
      setEditingName(false);
    } catch (e) {
      if (e instanceof ApiClientError && (e.status === 409 || e.code === 'ERR_CONFLICT')) {
        setNameError(t('errors.ERR_CONFLICT'));
      } else if (e instanceof ApiClientError) {
        setNameError(t(`errors.${e.code}`, e.message));
      } else {
        setNameError(t('errors.ERR_INTERNAL'));
      }
    } finally {
      setNameBusy(false);
    }
  };

  const saveNote = async () => {
    const trimmed = noteDraft.trim();
    setNoteBusy(true);
    setNoteError(null);
    try {
      await onEditNote(trimmed === '' ? null : trimmed);
      setEditingNote(false);
    } catch (e) {
      if (e instanceof ApiClientError) {
        setNoteError(t(`errors.${e.code}`, e.message));
      } else {
        setNoteError(t('errors.ERR_INTERNAL'));
      }
    } finally {
      setNoteBusy(false);
    }
  };

  const saveAlias = async () => {
    const next = aliasDraft.trim();
    if (next && !ALIAS_RE.test(next)) {
      setAliasError(t('admin.hutDuty.aliasTaken'));
      return;
    }
    setAliasBusy(true);
    setAliasError(null);
    try {
      await onEditAlias(next === '' ? null : next);
      setEditingAlias(false);
    } catch (e) {
      if (e instanceof ApiClientError && (e.status === 409 || e.code === 'ERR_CONFLICT')) {
        setAliasError(t('admin.hutDuty.aliasTaken'));
      } else if (e instanceof ApiClientError) {
        setAliasError(t(`errors.${e.code}`, e.message));
      } else {
        setAliasError(t('errors.ERR_INTERNAL'));
      }
    } finally {
      setAliasBusy(false);
    }
  };

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 first:border-t-0">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <div className="flex items-center gap-2 flex-wrap">
          {editingName ? (
            <span className="inline-flex items-center gap-1">
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                maxLength={120}
                autoFocus
                className="rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-0.5 text-sm font-medium w-40"
              />
              <button
                type="button"
                onClick={() => void saveName()}
                disabled={nameBusy}
                className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-2 py-0.5 text-xs"
              >
                {t('common.save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingName(false);
                  setNameDraft(row.name);
                  setNameError(null);
                }}
                disabled={nameBusy}
                className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-0.5 text-xs"
              >
                {t('common.cancel')}
              </button>
              {nameError && (
                <span className="text-xs text-rose-700 dark:text-rose-300">{nameError}</span>
              )}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                setNameDraft(row.name);
                setNameError(null);
                setEditingName(true);
              }}
              title={t('admin.hutDuty.name')}
              className="font-medium rounded-md px-1 -mx-1 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {row.name}
            </button>
          )}
          {editingNote ? (
            <span className="inline-flex items-center gap-1">
              <input
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                maxLength={500}
                autoFocus
                placeholder={t('admin.hutDuty.note')}
                className="rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-0.5 text-xs w-40"
              />
              <button
                type="button"
                onClick={() => void saveNote()}
                disabled={noteBusy}
                className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-2 py-0.5 text-xs"
              >
                {t('common.save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingNote(false);
                  setNoteDraft(row.note ?? '');
                  setNoteError(null);
                }}
                disabled={noteBusy}
                className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-0.5 text-xs"
              >
                {t('common.cancel')}
              </button>
              {noteError && (
                <span className="text-xs text-rose-700 dark:text-rose-300">{noteError}</span>
              )}
            </span>
          ) : row.note ? (
            <button
              type="button"
              onClick={() => {
                setNoteDraft(row.note ?? '');
                setNoteError(null);
                setEditingNote(true);
              }}
              title={t('admin.hutDuty.note')}
              className="text-xs text-slate-500 dark:text-slate-400 rounded-md px-1 -mx-1 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              — {row.note}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setNoteDraft('');
                setNoteError(null);
                setEditingNote(true);
              }}
              className="text-xs text-slate-500 dark:text-slate-400 rounded-md px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-800 border border-dashed border-slate-300 dark:border-slate-700"
            >
              {t('admin.hutDuty.addNote')}
            </button>
          )}
          <button
            type="button"
            onClick={onToggleActive}
            className={
              'inline-flex items-center rounded-full text-xs font-medium px-2 py-0.5 ' +
              (row.active
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200'
                : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300')
            }
          >
            {row.active ? t('admin.hutDuty.active') : t('admin.hutDuty.inactive')}
          </button>
          {editingAlias ? (
            <span className="inline-flex items-center gap-1">
              <input
                value={aliasDraft}
                onChange={(e) => setAliasDraft(e.target.value)}
                pattern={ALIAS_RE.source}
                placeholder="alpenrose-12"
                autoFocus
                className="rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-0.5 text-xs font-mono w-32"
              />
              <button
                type="button"
                onClick={() => setAliasDraft(randomAlias())}
                disabled={aliasBusy}
                className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-0.5 text-xs"
              >
                {t('admin.hutDuty.aliasSuggest')}
              </button>
              <button
                type="button"
                onClick={() => void saveAlias()}
                disabled={aliasBusy}
                className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-2 py-0.5 text-xs"
              >
                {t('common.save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingAlias(false);
                  setAliasDraft(row.login_alias ?? '');
                  setAliasError(null);
                }}
                disabled={aliasBusy}
                className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-0.5 text-xs"
              >
                {t('common.cancel')}
              </button>
              {aliasError && (
                <span className="text-xs text-rose-700 dark:text-rose-300">{aliasError}</span>
              )}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                setAliasDraft(row.login_alias ?? '');
                setAliasError(null);
                setEditingAlias(true);
              }}
              title={t('admin.hutDuty.alias')}
              className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs font-mono px-2 py-0.5 hover:bg-slate-200 dark:hover:bg-slate-700"
            >
              {row.login_alias ?? '—'}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onQr}
            className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-1 text-xs font-medium"
            title={t('admin.hutDuty.downloadQrHelp')}
          >
            {t('admin.hutDuty.downloadQr')}
          </button>
          <button
            type="button"
            onClick={() => setShowAdd((v) => !v)}
            className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-1 text-xs font-medium"
          >
            {showAdd ? t('common.cancel') : t('admin.hutDuty.addSlot')}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded-md border border-rose-300 bg-white text-rose-700 dark:border-rose-700 dark:bg-slate-900 dark:text-rose-300 px-2 py-1 text-xs font-medium"
          >
            {t('common.delete')}
          </button>
        </div>
      </div>

      <div className="px-3 pb-3 space-y-1">
        {row.slots.length === 0 && (
          <p className="text-xs text-slate-500 dark:text-slate-400 italic">{t('admin.hutDuty.noSlotsYet')}</p>
        )}
        {row.slots.length > displaySlots.length && (
          <p className="text-xs text-slate-500 dark:text-slate-400 italic">
            {t('admin.hutDuty.pastHidden', {
              count: row.slots.length - displaySlots.length,
            })}
          </p>
        )}
        {displaySlots.map((s) => (
          <SlotRow
            key={s.id}
            slot={s}
            lang={lang}
            autoOpen={targetSlotId === s.id}
            onEdit={(next) => onEditSlot(s.id, next)}
            onDelete={() => onDeleteSlot(s)}
          />
        ))}

        {showAdd && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 p-2 rounded-md bg-slate-50 border border-slate-200 dark:bg-slate-800 dark:border-slate-700">
            <SlotFields slot={slot} onChange={setSlot} />
            <div className="sm:col-span-2 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  onAddSlot(slot);
                  setSlot(blankSlot());
                  setShowAdd(false);
                }}
                className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function slotToDraft(s: HutDutySlot): NewSlotInput {
  return {
    check_in: s.check_in,
    check_out: s.check_out,
    note: s.note,
    beds_per_night: [...s.beds_per_night],
    is_duty_per_day: [...s.is_duty_per_day],
  };
}

function SlotRow({
  slot,
  lang,
  autoOpen,
  onEdit,
  onDelete,
}: {
  slot: HutDutySlot;
  lang: string;
  autoOpen?: boolean;
  onEdit(next: NewSlotInput): void;
  onDelete(): void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<NewSlotInput>(() => slotToDraft(slot));
  const rowRef = useRef<HTMLDivElement | null>(null);

  // Reset the draft to match upstream changes (e.g. after a successful save).
  useEffect(() => {
    if (!editing) setDraft(slotToDraft(slot));
  }, [slot, editing]);

  // Auto-open when the Calendar tab hands off this specific slot.
  useEffect(() => {
    if (autoOpen) {
      setEditing(true);
      requestAnimationFrame(() => {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [autoOpen]);

  return (
    <div ref={rowRef}>
      {editing ? (
        <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2 p-2 rounded-md bg-slate-50 border border-slate-200 dark:bg-slate-800 dark:border-slate-700">
          <SlotFields slot={draft} onChange={setDraft} />
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft(slotToDraft(slot));
                setEditing(false);
              }}
              className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                onEdit(draft);
                setEditing(false);
              }}
              className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium"
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 dark:border-slate-700 px-2 py-1 text-sm">
          <span className="tabular-nums">
            {formatDateShort(slot.check_in, lang)} – {formatDateShort(slot.check_out, lang)}
          </span>
          {/* One pill per covered day, in chronological order. Colour
              is amber when that day has duty, sky otherwise. Label
              includes the bed count when > 0 (the check_out day has no
              night and so no beds). Days with neither duty nor beds
              (e.g. a non-duty check_out day) are skipped so the strip
              shows only meaningful info — that's why there's no
              standalone "Aufenthalt" / "Dienst" summary pill. */}
          {dayDatesInclusive(slot.check_in, slot.check_out).map((date, i) => {
            const isDuty = slot.is_duty_per_day[i] ?? false;
            const beds = slot.beds_per_night[i] ?? 0;
            if (!isDuty && beds <= 0) return null;
            const tone = isDuty
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200'
              : 'bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200';
            const title = isDuty
              ? `${date}: ${t('admin.hutDuty.dutyLabel')}${beds > 0 ? ` · ${beds} ${t('admin.hutDuty.bedsShort')}` : ''}`
              : `${date}: ${beds} ${t('admin.hutDuty.bedsShort')}`;
            return (
              <span
                key={date}
                className={
                  'inline-flex items-center rounded-full text-xs font-medium px-2 py-0.5 tabular-nums ' +
                  tone
                }
                title={title}
              >
                {formatDateShort(date, lang)}
                {beds > 0 ? `: ${beds}` : ''}
              </span>
            );
          })}
          {slot.note && <span className="text-xs text-slate-500 dark:text-slate-400 flex-1 truncate">{slot.note}</span>}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-0.5 text-xs"
            >
              {t('common.edit')}
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="rounded-md border border-rose-300 bg-white text-rose-700 dark:border-rose-700 dark:bg-slate-900 dark:text-rose-300 px-2 py-0.5 text-xs"
              aria-label={t('common.delete')}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── drawers: single add + bulk import ──────────────────────────────── */

export function NewHutDutyDrawer({
  existingNames,
  initialSlot,
  onClose,
  onSaved,
}: {
  existingNames: ExistingNameRef[];
  initialSlot?: NewSlotInput;
  onClose(): void;
  onSaved(): void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async (
    name: string,
    note: string | null,
    slot: NewSlotInput,
    login_alias: string | null,
    existingId: number | null,
  ) => {
    if (slot.check_in >= slot.check_out) {
      setError(t('admin.hutDuty.slotInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (existingId !== null) {
        await apiAddSlot(existingId, slot);
      } else {
        await createHutDuty(name, note, slot, login_alias);
      }
      onSaved();
    } catch (e) {
      if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
      else setError(t('errors.ERR_INTERNAL'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 bg-slate-900/40 dark:bg-black/60 flex items-stretch justify-end">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex justify-between items-center">
          <h3 className="font-medium">{t('admin.hutDuty.add')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-1 text-sm"
            aria-label={t('common.cancel')}
          >
            ×
          </button>
        </div>
        <div className="p-4 space-y-3">
          {error && (
            <p
              role="alert"
              className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2"
            >
              {error}
            </p>
          )}
          <AddRowForm
            onAdd={handleAdd}
            busy={busy}
            existingNames={existingNames}
            initialSlot={initialSlot}
          />
        </div>
      </div>
    </div>
  );
}

function BulkImportDrawer({
  onClose,
  onSaved,
}: {
  onClose(): void;
  onSaved(skipped: number): void;
}) {
  const { t } = useTranslation();
  const [bulkText, setBulkText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const entries = bulkText
      .split(/\r?\n/)
      .map(parseBulkLine)
      .filter((e): e is BulkHutDutyEntry => e !== null);
    if (entries.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { skipped } = await bulkHutDuties(entries);
      onSaved(skipped);
    } catch (e) {
      if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
      else setError(t('errors.ERR_INTERNAL'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 bg-slate-900/40 dark:bg-black/60 flex items-stretch justify-end">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex justify-between items-center">
          <h3 className="font-medium">{t('admin.hutDuty.bulkImport')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-1 text-sm"
            aria-label={t('common.cancel')}
          >
            ×
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-600 dark:text-slate-400">
            {t('admin.hutDuty.bulkImportHelp')}
          </p>
          <textarea
            rows={10}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={'Müller\nSchmidt 2026-05-15 2026-05-17 4\nKeller'}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm font-mono"
          />
          {error && (
            <p
              role="alert"
              className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2"
            >
              {error}
            </p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              disabled={busy || !bulkText.trim()}
              onClick={() => void submit()}
              className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-60"
            >
              {t('admin.hutDuty.bulkAdd')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
